import {
    Box, Chip,
    Container, Divider, Grid, Typography,
} from '@mui/material';
import {
    AutoAwesome as AutoAwesomeIcon,
    Download, InsertEmoticon as InsertEmoticonIcon,
    Person as PersonIcon,
    SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
} from '@mui/icons-material';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import Anchor from '../foundation/Anchor';
import CustomBR from '../foundation/CustomBR';
import Page from '../layout/base.page';
import { CenteredFlex, Flexbox, VCenteredFlex } from '../foundation/Flex';
import {
    BGContainer, WhiteBGContainer,
} from '../foundation/Containers';
import { WatchCVPageProps } from '../../../contractTypes/pages/WatchCVPage.types';
import Button from '../foundation/Button';
import BackdropSpinner from '../foundation/BackdropSpinner';
import TextArea from '../foundation/TextArea';
import { postContactUs } from '../../api/contactus.api';
import Alert from '../foundation/Alert';
import newAPIService from '../../api/newAPIService';
import employerToJobState, { isEmployerProcessCompleted } from '../../../common/employer_to_job_state.enum';
import SimilarityGauge from '../components/SimilarityGauge';
import clientConfig from '../config.client';
import SimilaritiesBreakdown from '../components/SimilaritiesBreakdown';
import TextField from '../foundation/TextField';
import { systemUserApplyToJob } from '../../api/userToJob.api';
import userToJobStateEnum from '../../../common/user_to_job_state.enum';

const WatchCVPage = (props: WatchCVPageProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeRetryIntervalId = useRef<number>(null);
    const iframeFallbackTimeoutId = useRef<number>(null);
    const [iframeLoaded, setIframeLoaded] = React.useState(false);
    const [contactUsSuccess, setContactUsSuccess] = React.useState(false);
    const [employerResponse, setEmployerResponse] = React.useState(props.employerResponse || 0);
    const jobIdToSendApplicant = useRef<HTMLInputElement>();

    const onIframeLoaded = () => {
        setIframeLoaded(true);
        clearInterval(iframeRetryIntervalId.current);
        clearTimeout(iframeFallbackTimeoutId.current);
    };

    const getDownloadLink = () => {
        if (props.isJobApplication) {
            return `${clientConfig.baseUrl}employer/cv/${props.candidateId}/job/${props.jobId}?safeid=${props.safeid}&download=true`;
        }

        return `${clientConfig.baseUrl}anonymous-employer-view/cv/${props.cvId}?download=true`;
    };

    const getIframeSrc = () => `https://docs.google.com/gview?url=${getDownloadLink()}&embedded=true`;

    const updateIframeSrc = () => {
        if (iframeRef?.current) {
            iframeRef.current.src = getIframeSrc();
        }
    };

    useEffect(() => {
        iframeRetryIntervalId.current = window.setInterval(
            updateIframeSrc, 1000 * 3,
        );

        // Google Docs viewer is cross-origin; onLoad + fallback timeout replace document polling.
        iframeFallbackTimeoutId.current = window.setTimeout(onIframeLoaded, 15000);

        return () => {
            clearInterval(iframeRetryIntervalId.current);
            clearTimeout(iframeFallbackTimeoutId.current);
        };
    }, []);

    const getCV = () => {
        if (props.hasCV) {
            if (props.isImage) {
                if (!props.isJobApplication) {
                    return (
                        <img alt="קורות חיים"
                            src={`${clientConfig.baseUrl}anonymous-employer-view/cv/${props.cvId}?iframe=true`} style={{ width: '100%' }} />
                    );
                }
                return (
                    <img alt="קורות חיים"
                        src={`${clientConfig.baseUrl}employer/cv/${props.candidateId}/job/${props.jobId}?safeid=${props.safeid}&iframe=true`} style={{ width: '100%' }} />
                );
            }

            return <Box position="relative">
                <BackdropSpinner open={!iframeLoaded} style={{ position: 'absolute' }} />
                <iframe ref={iframeRef} title="קורות החיים" onLoad={onIframeLoaded}
                    onError={updateIframeSrc}
                    style={{ width: '100%', height: '700px', border: 0 }}
                    src={getIframeSrc()} />
            </Box>;
        }

        return null;
    };

    const renderKeyValue = (key, value) => {
        if (value && value !== '') {
            return <Typography><b>{key} : </b>{value}</Typography>;
        }
        return '';
    };

    const renderDontHaveCV = () => {
        if (!props.hasCV) {
            return <div><CustomBR /> <Typography>
                    המשתמש הגיש מועמדות למשרה זו, אך לא הגיש קובץ קורות חיים.
            </Typography></div>;
        }

        return '';
    };

    const renderDownloadCV = () => {
        if (!props.hasCV) return '';

        return <div>
            <CustomBR />
            <Anchor blank href={getDownloadLink()}>
                <VCenteredFlex><Download /> לחץ כאן להורדת קובץ קורות החיים </VCenteredFlex>
            </Anchor>
            <CustomBR />
        </div>;
    };

    const renderApplicationSourceBadge = () => {
        if (!props.isJobApplication) return null;

        const isSentByAI = props.userToJobState === userToJobStateEnum.LIKE_BY_SYSTEM;
        return (
            <Chip
                icon={isSentByAI ? <AutoAwesomeIcon /> : <PersonIcon />}
                label={isSentByAI ? 'הופנה ע״י מערכת AI' : 'הגיש/ה מועמדות באופן עצמאי'}
                color={isSentByAI ? 'secondary' : 'default'}
                variant={isSentByAI ? 'filled' : 'outlined'}
                size="small"
            />
        );
    };

    const renderCandidateDetails = () => <><CustomBR /><BGContainer>
        <Grid container spacing={2}>
            {!props.candidate ? <Grid item sm={12} md={8}>
                <VCenteredFlex style={{ gap: '8px' }}>
                    <Typography fontWeight="bold" style={{ textDecoration: 'underline' }}>פרטי המועמד</Typography>
                    {renderApplicationSourceBadge()}
                </VCenteredFlex>
                <CustomBR />
                {renderKeyValue('שם המועמד', props.firstName)}
                {renderKeyValue('אזורי חיפוש עבודה', props.candidateLocations?.join(', '))}
                <CustomBR />
                {renderKeyValue('תקציר קורות חיים', props.cvSummary)}
                <CustomBR />
                {renderKeyValue('הודעה מהמועמד', props.candidateMessage)}
                {renderDontHaveCV()}
                {renderDownloadCV()}
            </Grid> : <Grid item sm={12} md={8}>
                <VCenteredFlex style={{ gap: '8px' }}>
                    <Typography fontWeight="bold" style={{ textDecoration: 'underline' }}>פרטי המועמד</Typography>
                    {renderApplicationSourceBadge()}
                </VCenteredFlex>
                <CustomBR />
                {renderKeyValue('שם הפונה למשרה', props.candidate.firstname || `${props.candidate.lastname}` || '')}
                {renderKeyValue('מספר טלפון', props.candidate.phone)}
                {renderKeyValue('כתובת אימייל', props.candidate.email)}
                {renderKeyValue('אזורי חיפוש עבודה', props.candidateLocations?.join(', '))}
                {renderKeyValue('הודעה מהמועמד', props.candidateMessage)}
                {renderDontHaveCV()}
                {renderDownloadCV()}
            </Grid>}
            <Grid item sm={12} md={4}>
                {props.cvToJobSimilarity?.score > 0.1 && <WhiteBGContainer>
                    <CenteredFlex flexDirection="column">
                        <Typography variant="h6" color="secondary" fontWeight="bold">התאמה למשרה</Typography>
                        <CustomBR />
                        <SimilarityGauge similarity={props.cvToJobSimilarity.score} />
                        <SimilaritiesBreakdown {...props.cvToJobSimilarity} />
                    </CenteredFlex>

                </WhiteBGContainer>}
            </Grid>

        </Grid>
    </BGContainer></>;

    const onEmployerResponseClick = async (response: number) => {
        await newAPIService.get(`/employer/applicationResponse/${props.candidateId}/job/${props.jobId}?safeid=${props.safeid}&response=${response}`);
        setEmployerResponse(response);
    };

    const onSubmit = async data => {
        window.showBackdrop();
        await postContactUs({
            message: `
${data.message}
url: ${window.location.href}
jobId: ${props.jobId}
jobEmail: ${props.job?.email}
jobPhone: ${props.job?.phone}
candidateId: ${props.candidateId}
candidateName: ${props.candidate?.firstname} ${props.candidate?.lastname}
job createdby userid: ${props.createdByUserId}
            `,
            email: props.user?.email || props.job?.email,
            phone: props.job?.phone,
            subject: props.user ? `משוב ממעסיק - ${props.user?.firstname} ${props.user?.lastname}` : 'משוב ממעסיק',
            fullname: props.user ? `${props.user?.firstname} ${props.user?.lastname}` : '',
            userid: props.user?.userid || props.createdByUserId,
        });
        window.hideBackdrop();
        setContactUsSuccess(true);
    };

    const postCandidateToJob = async () => {
        try {
            const jobId = parseInt(jobIdToSendApplicant.current.value, 10);
            await systemUserApplyToJob(
                { userid: props.candidateId, jobid: jobId },
            );
            window.showGlobalModal('הגשת מועמדות', `המועמד ${props.firstName} הופנה בהצלחה למעסיק השייך למשרה ${jobId}`);
        } catch (error) {
            window.showGlobalModal('accounted error', `${error.toString()} | ${error.response?.data}`);
        }
    };

    const {
        register, handleSubmit,
    } = useForm();

    const messageInput = register('message', { required: true });

    return (
        <Page pageHeaderTitle="Mploy - צפייה במועמדות למשרה" url={props.url} user={props.user}>
            <Container>
                <CustomBR />
                {props.isJobApplication && <CenteredFlex>
                    <Typography color="secondary" fontWeight="bold" variant="h4">התקבלה מועמדות חדשה&nbsp;</Typography>
                    <Anchor color="secondary" fontWeight="bold" variant="h4" href={`/job/details/${props.jobId}`} blank style={{ textDecoration: 'underline' }}>
                        <>למשרה - {props.job?.title} {props.job?.employerJobid}</>
                    </Anchor>
                </CenteredFlex>}
                <CustomBR />
                {/* {renderCreditBank()}
                <CustomBR /> */}
                {renderCandidateDetails()}
                <CustomBR />
                <Divider />
                <CustomBR />
                {props.user?.admin && <BGContainer>
                    <Typography fontWeight="bold" variant="h5" color="secondary">אדמין</Typography>
                    <CustomBR />
                    <Box display="inline">
                        <TextField
                            inputRef={jobIdToSendApplicant}
                            name="jobIdToSendApplicant"
                            label="מספר משרה"
                            type="number"
                            size="small"
                        />
            &nbsp;&nbsp;
                        <Button onClick={postCandidateToJob}>שלח מועמד למשרה</Button>
                    </Box>
                </BGContainer>}
                {props.isJobApplication && <BGContainer>
                    {employerResponse === 2 && <Typography
                        color="secondary" fontWeight="bold" variant="body1">
                            תודה רבה על המשוב החיובי!
                            שמחים לשמוע שהמועמד רלוונטי עבורך – נמשיך לעבוד כדי להביא לך מועמדים מדויקים ואיכותיים נוספים.
                    </Typography>}
                    {employerResponse === 3 && <Typography color="secondary" fontWeight="bold" variant="body1">
                            ציינת שהמועמד אינו מתאים – תודה על המשוב! נחדד את ההתאמות הבאות בהתאם למה שציינת, כדי שתקבל מועמדים מדויקים יותר.
                    </Typography>}
                    {employerResponse === employerToJobState.ADVANCED_PROCESS && <Typography
                        color="secondary" fontWeight="bold" variant="body1">
                            המועמדות נמצאת בתהליך מתקדם.
                    </Typography>}
                    {isEmployerProcessCompleted(employerResponse) && <Typography
                        color="secondary" fontWeight="bold" variant="body1">
                            תהליך המועמדות הסתיים.
                    </Typography>}
                    {(!employerResponse || employerResponse <= employerToJobState.WATCHED) && <Grid container spacing={2}>
                        <Grid item md={6} sm={12}>
                            <Button
                                fullWidth
                                color="primary"
                                variant="contained" onClick={() => onEmployerResponseClick(2)}>
                                <Flexbox>
                                    <Typography color="white" fontWeight="bold">המועמד רלוונטי למשרה&nbsp;</Typography>
                                    <InsertEmoticonIcon />
                                </Flexbox>
                            </Button>
                        </Grid>
                        <Grid item md={6} sm={12}>
                            <Button
                                fullWidth
                                style={{ backgroundColor: '#b91f0b' }}
                                variant="contained" onClick={() => onEmployerResponseClick(3)}>
                                <Flexbox>
                                    <Typography color="white" fontWeight="bold">המועמד לא רלוונטי למשרה&nbsp;</Typography>
                                    <SentimentVeryDissatisfiedIcon />
                                </Flexbox>
                            </Button>
                        </Grid>
                    </Grid>}
                </BGContainer>}
                <div style={{ textAlign: 'center' }}>
                    <CustomBR />
                    <Typography color="secondary" fontWeight="bold" variant="h5">קורות חיים</Typography>
                    <CustomBR />
                    {getCV()}
                </div>
                <CustomBR />
                <CustomBR />
                {contactUsSuccess ? <Alert severity="success">ההודעה נשלחה בהצלחה! נחזור אליך במהרה.</Alert> : <Box>
                    <Typography fontWeight="bold" variant="h5">שתף אותנו במשוב מהיר</Typography>
                    <CustomBR />
                    <Typography variant="body2">
                        נשמח לשמוע את דעתך בכל נושא, ולעזור בתהליך הגיוס,
                        אנו זמינים <Anchor blank href="https://wa.me/972544700720">גם בוואסאפ - לחצו כאן לשליחת הודעה</Anchor>.
                    </Typography>
                    <CustomBR />
                    <div>
                        <TextArea
                            required
                            {...messageInput}
                            inputRef={messageInput.ref}
                            name="message"
                            id="message"
                            label="הודעה"
                            type="text"
                            fullWidth
                            rows={2}
                        />
                    </div>
                    <CustomBR />
                    <Button onClick={handleSubmit(onSubmit)} color="primary" fullWidth>שלח הודעה</Button>
                </Box>}
                <CustomBR />
                <CustomBR />
                {props.isJobApplication && (
                    employerResponse === employerToJobState.EMPLOYER_WILL_CONTACT
                    || employerResponse === employerToJobState.ADVANCED_PROCESS
                ) && <Box>
                    <Typography fontWeight="bold" variant="h5">פעולות נוספות</Typography>
                    <CustomBR />
                    <Typography fontWeight="bold" variant="body1">
                            תהליך הגיוס הסתיים?
                        <Anchor blank href={`/employer/applicationResponse/${props.candidateId}/job/${props.jobId}?safeid=${props.safeid}&response=4`}>
                                &nbsp;לחץ כאן לסיום התהליך
                        </Anchor>
                    </Typography>
                </Box>}
                <CustomBR />
                <CustomBR />
            </Container>
        </Page>
    );
};

export default WatchCVPage;
